<template>
  <div class="report-card-viewer-wrapper">
    <!-- شريط الأدوات للتحكم في الطباعة -->
    <div class="print-toolbar">
      <el-button type="primary" @click="printReportCards" size="large">
        <el-icon><Printer /></el-icon> طباعة الشهادات
      </el-button>
      <el-button @click="$router.go(-1)" size="large">العودة</el-button>
    </div>

    <!-- عرض البيانات عند التحميل -->
    <div v-if="loading" v-loading="loading" element-loading-text="جاري تحضير الشهادات..." style="height: 200px;"></div>
    <div v-if="!loading && reportData.length === 0" class="no-data">
        <el-empty description="لا توجد بيانات لعرضها. قد يكون خطأ قد حدث." />
    </div>

    <!-- المحتوى الذي سيتم طباعته -->
    <div id="printable-content">
      <div v-for="studentReport in processedReportData" :key="studentReport.student_info.id" class="report-card-page landscape">
        <!-- 1. الهيدر من القالب -->
        <header v-if="studentReport.processed_header" v-html="studentReport.processed_header" class="report-header"></header>
        
        <!-- 2. جسم الشهادة -->
        <main class="report-body">
          <!-- جدول الدرجات -->
          <table class="grades-table">
            <thead>
              <tr>
                <th>المادة</th>
                <th>الدرجة النهائية (%)</th>
                <th>التقدير</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="grade in studentReport.grades" :key="grade.id">
                <td>{{ grade.course_offering.subject.name }}</td>
                <td>{{ grade.weighted_average_score_percentage }}</td>
                <td>{{ grade.grading_scale_entry?.grade_label || 'N/A' }}</td>
              </tr>
            </tbody>
          </table>

          <!-- الأقسام الإضافية بناءً على خيارات القالب -->
          <div class="bottom-sections-container">
              <div v-if="showGradingPolicy" class="grading-policy-section">
                  <h4>سياسة التقييم المعتمدة</h4>
                  <div v-if="gradingPolicy && gradingPolicy.components">
                      <el-descriptions :column="1" border size="small">
                          <el-descriptions-item v-for="component in gradingPolicy.components" :key="component.id">
                              <template #label>{{ component.assessment_type.name }}</template>
                              {{ component.weight_percentage }}%
                          </el-descriptions-item>
                      </el-descriptions>
                  </div>
              </div>

              <div v-if="showGradingScaleKey" class="grading-scale-section">
                  <h4>مفتاح التقديرات</h4>
                  <table>
                      <tbody>
                          <tr>
                              <th v-for="scale in gradingScales" :key="scale.grade_label">{{ scale.grade_label }}</th>
                          </tr>
                          <tr>
                              <td v-for="scale in gradingScales" :key="scale.min_percentage">{{ formatScore(scale.min_percentage) }}+</td>
                          </tr>
                      </tbody>
                  </table>
              </div>

              <div v-if="showAttendance" class="section">
                  <h4>ملخص الحضور والغياب</h4>
                  <p>أيام الغياب: {{ studentReport.attendance.absent_days }} | أيام التأخر: {{ studentReport.attendance.late_days }}</p>
              </div>
              <div v-if="showTeacherRemarks && studentReport.comments.length > 0" class="section">
                  <h4>ملاحظات عامة</h4>
                  <ul>
                      <li v-for="comment in studentReport.comments" :key="comment.id">
                          {{ comment.comment_text }} ({{ comment.commenter_role || 'إدارة' }})
                      </li>
                  </ul>
              </div>
               <div v-if="showBehavioralEvaluation && Object.keys(studentReport.evaluations).length > 0" class="section">
                  <h4>تقييم السلوك والمهارات</h4>
                  <div v-for="(items, area) in studentReport.evaluations" :key="area">
                      <h5>{{ area }}</h5>
                      <ul>
                          <li v-for="item in items" :key="item.id">
                             {{ item.evaluation_item.name }}: <strong>{{ item.evaluation_value }}</strong>
                          </li>
                      </ul>
                  </div>
              </div>
          </div>
        </main>

        <!-- 3. التذييل من القالب -->
        <footer v-if="studentReport.processed_footer" v-html="studentReport.processed_footer" class="report-footer"></footer>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { ElMessage } from 'element-plus';
import { Printer } from '@element-plus/icons-vue';

export default {
  name: 'ReportCardViewer',
  components: { Printer },
  data() {
    return {
      api: null,
      loading: true,
      reportData: [],
      template: {
        header_content: '',
        footer_content: '',
        layout_options: {},
        logo_path: null,
      },
      logoBase64: null,
      gradingScales: [],
      gradingPolicy: null,
    };
  },
  computed: {
    showGradingPolicy() { return this.template.layout_options?.show_grading_policy; },
    showGradingScaleKey() { return this.template.layout_options?.show_grading_scale_key; },
    showGpa() { return this.template.layout_options?.show_gpa; },
    showAttendance() { return this.template.layout_options?.show_attendance; },
    showBehavioralEvaluation() { return this.template.layout_options?.show_behavioral_evaluation; },
    showTeacherRemarks() { return this.template.layout_options?.show_teacher_remarks; },

    processedReportData() {
      if (!this.reportData.length) return [];
      return this.reportData.map(studentReport => {
        const process = (content) => {
          if (!content) return '';
          let processed = content
            .replace(/{{student_name}}/g, studentReport.student_info.name || '')
            .replace(/{{class_name}}/g, studentReport.student_info.class_name || '')
            .replace(/{{study_year_name}}/g, studentReport.term_info.study_year || '')
            .replace(/{{term_name}}/g, studentReport.term_info.name || '');
          
          if (this.logoBase64 && this.logoBase64 !== 'placeholder') {
            processed = processed.replace(/<img[^>]+src="([^">]+)"/g, `<img src="${this.logoBase64}" alt="School Logo" style="width: 180px; height: 80px;margin-bottom:10px;">`);
          }
          return processed;
        };

        return {
          ...studentReport,
          processed_header: process(this.template.header_content),
          processed_footer: process(this.template.footer_content),
        };
      });
    }
  },
  created() {
    this.api = axios.create({ baseURL: process.env.VUE_APP_API_BASE_URL || 'https://cale.caledonian.ly/api' });
    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem('access_token');
      if (token) config.headers['Authorization'] = `Bearer ${token}`;
      return config;
    });

    this.fetchDataForPrinting();
  },
  methods: {
    async fetchDataForPrinting() {
      const { class_id, term_id, template_id } = this.$route.query;
      if (!class_id || !term_id || !template_id) {
        ElMessage.error('المعلومات المطلوبة غير كاملة لعرض الشهادات.');
        this.loading = false;
        return;
      }
      
      this.loading = true;
      try {
        const [reportDataRes, templateRes] = await Promise.all([
          this.api.post('/report-cards/generate-data', { class_id, term_id }),
          this.api.get(`/report-card-templates/${template_id}`)
        ]);
        
        this.reportData = reportDataRes.data.students_reports;
        this.gradingScales = reportDataRes.data.grading_scales;
        this.gradingPolicy = reportDataRes.data.grading_policy;
        this.template = templateRes.data;
        
        if (this.template.logo_path) {
            await this.fetchAndEncodeLogo(this.template.logo_path);
        } else {
            this.logoBase64 = 'placeholder';
        }

      } catch (error) {
        ElMessage.error('فشل في جلب بيانات الشهادات.');
      } finally {
        this.loading = false;
      }
    },
    async fetchAndEncodeLogo(logoPath) {
      if(!logoPath) {
           this.logoBase64 = 'placeholder';
           return;
      }
      try {
          const fullUrl = `${this.api.defaults.baseURL.replace('/api', '')}/storage/${logoPath}`;
          const response = await axios.get(fullUrl, { responseType: 'blob' });
          const reader = new FileReader();
          reader.readAsDataURL(response.data);
          reader.onloadend = () => { this.logoBase64 = reader.result; };
      } catch (error) {
          this.logoBase64 = 'https://cale.caledonian.ly/logo.png';
      }
    },
    formatScore(score) {
        if (score === null || score === undefined) return '-';
        const numScore = parseFloat(score);
        return isNaN(numScore) ? '-' : numScore.toFixed(2);
    },
    printReportCards() {
      const printableContent = document.getElementById('printable-content').innerHTML;
      const printStyles = `
          <style>
            body {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            @page {
              size: A4 landscape;
              margin: 0;
            }
            .report-card-page {
                background-color: white;
                width: 297mm;
                height: 209mm; /* A bit less to be safe */
                margin: 0 !important;
                padding: 10mm !important;
                box-shadow: none !important;
                box-sizing: border-box !important;
                page-break-after: always;
                display: flex;
                flex-direction: column;
                position: relative;
                font-family: 'Tajawal', sans-serif;
            }
            .report-card-page:last-child {
                page-break-after: auto;
            }
            .report-header, .report-footer { direction: rtl; flex-shrink: 0; }
            .report-body { flex-grow: 1; }
            .grades-table {
              width: 100%; border-collapse: collapse; text-align: center; direction: rtl; margin-top: 20px;
            }
            .grades-table th, .grades-table td {
              border: 1px solid #ddd; padding: 8px; font-size: 10pt;
            }
            .grades-table thead { background-color: #409EFF !important; color: white !important; }
            .bottom-sections-container {
              display: flex; justify-content: space-between; margin-top: 20px; gap: 15px; align-items: flex-start;
            }
            .section, .grading-policy-section, .grading-scale-section {
              flex: 1; padding: 10px; border: 1px solid #e0e0e0; border-radius: 8px; background-color: #f9f9f9 !important;
            }
            h4 {
              border-bottom: 1px solid #eee; padding-bottom: 5px; margin: 0 0 10px 0; font-size: 12pt;
            }
            .grading-scale-section table { width: 100%; text-align: center; border-collapse: collapse; }
            .grading-scale-section th, .grading-scale-section td {
              padding: 4px; font-size: 9pt; border: 1px solid #eee;
            }
          </style>
      `;

      const printWindow = window.open('', '_blank');
      printWindow.document.write('<html><head><title>طباعة الشهادات</title>');
      printWindow.document.write('<link href="https://fonts.googleapis.com/css2?family=Tajawal:wght@400;700&display=swap" rel="stylesheet">');
      printWindow.document.write(printStyles);
      printWindow.document.write('</head><body>');
      printWindow.document.write(printableContent);
      printWindow.document.write('</body></html>');
      
      printWindow.document.close();
      
      setTimeout(() => {
        printWindow.focus();
        printWindow.print();
        printWindow.close();
      }, 500);
    },
  },
};
</script>

<style>
/* --- أنماط العرض على الشاشة --- */
.report-card-viewer-wrapper { background-color: #f0f2f5; }
.print-toolbar {
  background-color: white; padding: 10px; margin: 0 20px 20px 20px;
  border-radius: 8px; box-shadow: 0 2px 12px 0 rgba(0,0,0,0.1); text-align: center;
}
#printable-content { padding: 20px; }

.report-card-page.landscape {
  background-color: white;
  width: 297mm;
  min-height: 210mm;
  margin: 0 auto 20px auto;
  padding: 15mm;
  box-shadow: 0 0 10px rgba(0,0,0,0.15);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  position: relative;
  font-family: 'Tajawal', sans-serif;
}

.report-header, .report-footer { direction: rtl; flex-shrink: 0; }
.report-body { flex-grow: 1; }
.report-footer { margin-top: auto; }
.grades-table {
  width: 100%; border-collapse: collapse; text-align: center; direction: rtl; margin-top: 20px;
}
.grades-table th, .grades-table td {
  border: 1px solid #ddd; padding: 8px; font-size: 14px;
}
.grades-table thead { background-color: #409EFF; color: white; }

.bottom-sections-container {
  display: flex;
  justify-content: space-between;
  margin-top: 30px;
  gap: 20px;
  align-items: flex-start;
}
.section, .grading-policy-section, .grading-scale-section {
  flex: 1;
  padding: 15px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background-color: #f9f9f9;
}
.section h4, .grading-policy-section h4, .grading-scale-section h4 {
  border-bottom: 1px solid #eee; padding-bottom: 5px; margin-bottom: 10px;
}
.section ul { list-style-type: none; padding-right: 10px; }
.grading-scale-section table { width: 100%; text-align: center; border-collapse: collapse; }
.grading-scale-section th, .grading-scale-section td {
  padding: 5px; font-size: 12px; border: 1px solid #eee;
}

/* --- Styles for Printing (using @page) --- */
@media print {
  .print-toolbar {
    display: none !important;
  }
}
</style>
